import React, { useEffect, useRef } from 'react';
import { entitlementsApi } from '../../services/entitlementsApi';

/** In-app workspaces the gate can be mounted for. Corporate is NOT here: it is
 *  a separate app reached by SSO, so it never renders an in-app gate. */
export type TrialGateRole = 'criminal' | 'civil';

/** Left behind in browsers by the old client-side trial flag. Removed on
 *  sight: it was per BROWSER, not per account, so one account's trial
 *  silently stopped every later account on the same browser from getting one
 *  (2026-10-17, a new dev account stayed on "Free"). */
const LEGACY_TRIAL_FLAG = 'nyayabodh_trial_gate_settled';

/**
 * Make sure the signed-in lawyer's trial exists — by asking the SERVER.
 *
 * The server owns trials (owner decision 2026-10-17: there is no "Free";
 * every account is on a trial or a paid plan). GET /entitlements/me registers
 * the 30-day trial for each practice the lawyer has chosen, on first visit;
 * any gated action registers it for the practice being used. The browser
 * keeps NO record of its own — that flag is what lost trials before.
 *
 * Fire-and-forget by contract: never blocks entering a workspace.
 */
export async function ensureTrialOnServer(): Promise<void> {
  try {
    localStorage.removeItem(LEGACY_TRIAL_FLAG);
  } catch {
    /* storage blocked — nothing to clean */
  }
  try {
    await entitlementsApi.getMine();
  } catch (e) {
    console.error('[trial] entitlement check failed — the server retries on next use', e);
  }
}

interface TrialGateProps {
  role: TrialGateRole;
  /** Always called exactly once, whatever the outcome — never blocks entry. */
  onDone: () => void;
}

/**
 * Headless onboarding step for criminal/civil: lets the server register the
 * trial for the practice just chosen, then releases the caller. Renders
 * nothing. Awaited so the plan badge reads the trial on first paint.
 */
const TrialGate: React.FC<TrialGateProps> = ({ onDone }) => {
  // React 18 StrictMode double-invokes effects in dev.
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    void ensureTrialOnServer().finally(onDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default TrialGate;
